import { useState, useEffect, useRef } from 'react';
import { isCapacitorNative } from '@/lib/capacitor';

/**
 * Returns whether the app/page is visible and active.
 * Use this to pause animations when the app is in background or tab is hidden,
 * reducing CPU/GPU usage and preventing overheating on mobile.
 *
 * - On web: follows document.visibilityState (tab visible/hidden).
 * - On Capacitor (iOS/Android): also follows native app foreground/background
 *   when @capacitor/app is available.
 */
export function usePageVisibility(): boolean {
  const [documentVisible, setDocumentVisible] = useState(
    () => (typeof document !== 'undefined' ? document.visibilityState === 'visible' : true)
  );
  const [appActive, setAppActive] = useState(true);

  useEffect(() => {
    const handleVisibility = () => {
      setDocumentVisible(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // On native, also listen to Capacitor App lifecycle so we pause when app is backgrounded
  // (document.visibilityState may not update when app is sent to background on iOS)
  const removeRef = useRef<(() => Promise<void>) | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isCapacitorNative()) return;

    cancelledRef.current = false;

    import('@capacitor/app')
      .then(({ App }) => {
        return App.addListener('appStateChange', ({ isActive }) => {
          setAppActive(isActive);
        });
      })
      .then((listener) => {
        if (cancelledRef.current) {
          listener.remove();
          return;
        }
        removeRef.current = listener.remove;
      })
      .catch(() => {
        // @capacitor/app not installed or failed – rely on document.visibilityState only
      });

    return () => {
      cancelledRef.current = true;
      removeRef.current?.();
      removeRef.current = null;
    };
  }, []);

  return documentVisible && appActive;
}
